import { makeStyles, mergeClasses, Popover, PopoverSurface, PopoverTrigger, tokens } from '@fluentui/react-components';
import { ChevronLeft16Regular, ChevronRight16Regular } from '@fluentui/react-icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentMetadata } from '../../../libs/models/ContentMetadata';
import { resolveCitedSources } from '../../utils/ChatMessageUtils';
import { SourceFavicon } from './SourceFavicon';
import { getHostname, isSafeUrl, sanitizeTitle } from './sourceUtils';
import { withReferralParams } from '../../../libs/utils/referralTracking';

const useClasses = makeStyles({
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        height: '18px',
        paddingLeft: '3px',
        paddingRight: '6px',
        marginLeft: '2px',
        marginRight: '2px',
        backgroundColor: tokens.colorNeutralBackground3,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: tokens.fontWeightMedium,
        color: tokens.colorNeutralForeground2,
        cursor: 'pointer',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
        appearance: 'none' as const,
        lineHeight: '1',
        transition: 'background 160ms, border-color 160ms, color 160ms',
        ':hover': {
            backgroundColor: tokens.colorBrandBackground2,
            border: `1px solid ${tokens.colorBrandStroke2}`,
            color: tokens.colorBrandForeground2,
        },
    },
    chipActive: {
        backgroundColor: tokens.colorBrandBackground2,
        border: `1px solid ${tokens.colorBrandStroke2}`,
        color: tokens.colorBrandForeground2,
    },
    chipMore: {
        color: tokens.colorNeutralForeground3,
        fontWeight: tokens.fontWeightMedium,
        paddingLeft: '3px',
        borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
        marginLeft: '2px',
        fontSize: '10px',
    },
    popoverSurface: {
        padding: tokens.spacingHorizontalS,
        width: '280px',
    },
    popoverHead: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginBottom: tokens.spacingVerticalS,
    },
    navButton: {
        appearance: 'none' as const,
        background: 'transparent',
        border: 0,
        width: '24px',
        height: '24px',
        borderRadius: tokens.borderRadiusSmall,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.colorNeutralForeground2,
        cursor: 'pointer',
        padding: 0,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorNeutralForeground1,
        },
        ':focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: tokens.colorStrokeFocus2,
            outlineOffset: '2px',
        },
        ':disabled': {
            color: tokens.colorNeutralForegroundDisabled,
            cursor: 'default',
        },
    },
    popoverCount: {
        marginLeft: 'auto',
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightMedium,
        color: tokens.colorNeutralForeground3,
        fontVariantNumeric: 'tabular-nums',
    },
    popoverBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        textDecoration: 'none',
        color: tokens.colorNeutralForeground1,
        outline: 'none',
        ':hover': {
            textDecoration: 'none',
        },
        ':focus-visible': {
            outlineWidth: '2px',
            outlineStyle: 'solid',
            outlineColor: tokens.colorStrokeFocus2,
            outlineOffset: '2px',
        },
    },
    popoverSource: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightMedium,
    },
    popoverTitle: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        letterSpacing: '-0.005em',
    },
    popoverTitleHover: {
        ':hover .source-chip-title': {
            color: tokens.colorBrandForegroundLink,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
        },
    },
});

interface SourceChipProps {
    refIds: string;
    contentMetadata: ContentMetadata[];
}

// Short label shown on the collapsed chip: the first DNS label of the source hostname
// (e.g. "support" from support.visma.com). When the source has no URL — the backend emits null URLs
// when the SDK can't parse one — fall back to a trimmed, tag-free slice of the title so the chip
// never renders blank.
function chipLabel(hostname: string, title: string | undefined): string {
    if (hostname) {
        return hostname.replace(/^www\./, '').split('.')[0];
    }
    const text = (title ?? '').replace(/<[^>]*>/g, '').trim();
    return text.length > 24 ? `${text.slice(0, 24).trimEnd()}…` : text;
}

export const SourceChip: React.FC<SourceChipProps> = ({ refIds, contentMetadata }) => {
    const classes = useClasses();
    const { t } = useTranslation();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [popoverOpen, setPopoverOpen] = useState(false);

    const items = useMemo(() => resolveCitedSources(refIds, contentMetadata), [refIds, contentMetadata]);

    if (items.length === 0) {
        // Orphan/unknown marker: leave an empty <sup> in place rather than vanishing entirely, the
        // same #1706 guarantee the legacy numbered path keeps (the sup override renders this
        // component for every [[#n]] marker).
        return <sup />;
    }

    const first = items[0];
    const firstHostname = first.url ? getHostname(first.url) : '';
    const displayName = chipLabel(firstHostname, first.title);
    const extra = items.length - 1;
    const active = items[currentIndex] ?? first;
    const activeHostname = active.url ? getHostname(active.url) : '';

    return (
        <Popover
            open={popoverOpen}
            onOpenChange={(_e, data) => {
                setPopoverOpen(data.open);
                if (data.open) {
                    setCurrentIndex(0);
                }
            }}
            positioning={{ position: 'above' }}
            openOnHover
            mouseLeaveDelay={300}
            withArrow
        >
            <PopoverTrigger disableButtonEnhancement>
                <button
                    className={mergeClasses(classes.chip, popoverOpen ? classes.chipActive : undefined)}
                    aria-expanded={popoverOpen}
                >
                    <SourceFavicon hostname={firstHostname} variant="chip" />
                    <span>{displayName}</span>
                    {extra > 0 && <span className={classes.chipMore}>+{extra}</span>}
                </button>
            </PopoverTrigger>
            <PopoverSurface className={classes.popoverSurface}>
                {items.length > 1 && (
                    <div className={classes.popoverHead}>
                        <button
                            className={classes.navButton}
                            disabled={currentIndex === 0}
                            onClick={() => {
                                setCurrentIndex((n) => Math.max(0, n - 1));
                            }}
                            aria-label={t('chathistorysourcelinks.prevSource')}
                        >
                            <ChevronLeft16Regular />
                        </button>
                        <button
                            className={classes.navButton}
                            disabled={currentIndex >= items.length - 1}
                            onClick={() => {
                                setCurrentIndex((n) => Math.min(items.length - 1, n + 1));
                            }}
                            aria-label={t('chathistorysourcelinks.nextSource')}
                        >
                            <ChevronRight16Regular />
                        </button>
                        <span className={classes.popoverCount}>
                            {currentIndex + 1}/{items.length}
                        </span>
                    </div>
                )}
                {isSafeUrl(active.url) ? (
                    <a
                        className={mergeClasses(classes.popoverBody, classes.popoverTitleHover)}
                        href={withReferralParams(active.url, 'citation')}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <div className={classes.popoverSource}>
                            <SourceFavicon hostname={activeHostname} variant="popover" />
                            <span>{activeHostname}</span>
                        </div>
                        <span
                            className={`${classes.popoverTitle} source-chip-title`}
                            dangerouslySetInnerHTML={{ __html: sanitizeTitle(active.title) }}
                        />
                    </a>
                ) : (
                    <div className={classes.popoverBody}>
                        <div className={classes.popoverSource}>
                            <SourceFavicon hostname={activeHostname} variant="popover" />
                            <span>{activeHostname}</span>
                        </div>
                        <span
                            className={classes.popoverTitle}
                            dangerouslySetInnerHTML={{ __html: sanitizeTitle(active.title) }}
                        />
                    </div>
                )}
            </PopoverSurface>
        </Popover>
    );
};
